import { PaperClipIcon } from "@heroicons/react/24/outline";
import { type FC } from "react";

import type { RioAttachmentPreview } from "@api/rest/types-and-hooks";

interface Props {
  attachments: RioAttachmentPreview[];
}

/**
 * Attachment previews for a Rio USER message bubble. Images preview
 * inline; anything else renders as a filename chip linked to the
 * signed URL.
 */
export const RioAttachmentPreviewList: FC<Props> = ({ attachments }) => {
  if (!attachments.length) return null;
  return (
    <div className="mt-2 flex flex-col gap-2">
      {attachments.map((a) => {
        const isImage = a.mime_type.startsWith("image/");
        // Server hydrates a fresh signed URL per read; on failure it
        // sends "" so we can still show the filename/mime as a plain
        // chip instead of dropping the attachment.
        const hasUrl = Boolean(a.signed_url);
        if (isImage && hasUrl) {
          return (
            <a
              key={a.file_uuid}
              href={a.signed_url}
              target="_blank"
              rel="noopener noreferrer"
              title={a.filename}
              className="block"
            >
              <img
                src={a.signed_url}
                alt={a.filename}
                className="max-h-48 max-w-full rounded border border-border-subtle"
              />
            </a>
          );
        }
        const chipClass =
          "inline-flex max-w-full items-center gap-1 self-start rounded border border-border-subtle bg-bg-default px-2 py-1 text-xs text-text-primary";
        if (hasUrl) {
          return (
            <a
              key={a.file_uuid}
              href={a.signed_url}
              target="_blank"
              rel="noopener noreferrer"
              title={a.filename}
              className={`${chipClass} hover:bg-limestone-50`}
            >
              <PaperClipIcon className="size-3.5 shrink-0 text-text-tertiary" />
              <span className="truncate">{a.filename}</span>
            </a>
          );
        }
        return (
          <span
            key={a.file_uuid}
            title={`${a.filename} — preview unavailable`}
            className={`${chipClass} text-text-tertiary`}
          >
            <PaperClipIcon className="size-3.5 shrink-0" />
            <span className="truncate">{a.filename}</span>
          </span>
        );
      })}
    </div>
  );
};
